/**
 * ConfirmModal Component
 *
 * Reusable confirmation modal to replace window.confirm() and window.alert()
 */

import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  isProcessing?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'warning',
  isProcessing = false,
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: 'bg-red-500/20',
          iconColor: 'text-red-400',
          confirmButton: 'bg-red-500 hover:bg-red-600 text-white',
        };
      case 'warning':
        return {
          iconBg: 'bg-yellow-500/20',
          iconColor: 'text-yellow-400',
          confirmButton: 'bg-yellow-500 hover:bg-yellow-600 text-black',
        };
      case 'info':
        return {
          iconBg: 'bg-blue-500/20',
          iconColor: 'text-blue-400',
          confirmButton: 'bg-[#F4CC04] hover:bg-[#D4B104] text-black',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-50"
        onClick={!isProcessing ? onClose : undefined}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl shadow-2xl p-6 z-50">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`flex items-center justify-center w-12 h-12 ${styles.iconBg} rounded-lg flex-shrink-0`}>
            <AlertTriangle className={`w-6 h-6 ${styles.iconColor}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-white mb-2">{title}</h2>
            <p className="text-sm text-[#cccccc] whitespace-pre-wrap">{message}</p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-1 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="
              px-4 py-2
              text-[#888888] hover:text-white
              hover:bg-[#333333]
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className={`
              px-4 py-2 font-medium
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center gap-2
              ${styles.confirmButton}
            `}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                <span>Processando...</span>
              </>
            ) : (
              <span>{confirmText}</span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ConfirmModal;
